import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';
import { salesApi } from '../../services/salesApi.js';
import { orderPdfAttachment } from '../../utils/pdf.js';

/**
 * E-mails the order confirmation PDF via the configured SMTP server
 * (finance /email/send). Unlike quotes there is no status flip - the
 * confirmation is a document of an already CONFIRMED order. Pass any
 * order row (`{id, orderNumber}`) - the detail incl. lines is fetched
 * here so the list page can use the modal too.
 */
export function OrderSendModal({ order, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const open = Boolean(order);
  const id = order?.id;

  const detail = useQuery({
    queryKey: ['sales', 'orders', id],
    queryFn: () => salesApi.getOrder(id).then((r) => r.items),
    enabled: open,
  });
  const customers = useQuery({
    queryKey: ['sales', 'customers'],
    queryFn: () => salesApi.listCustomers().then((r) => r.items || []),
    enabled: open,
  });
  const settings = useQuery({
    queryKey: ['finance', 'company-settings'],
    queryFn: () => financeApi.getCompanySettings().then((r) => r.items),
    retry: false,
    enabled: open,
  });

  const customer = (customers.data || []).find((c) => c.id === detail.data?.customerId);

  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  useEffect(() => {
    if (!open || !detail.data) return;
    const o = detail.data;
    setTo(customer?.email || '');
    const base = t('order.email.subject', { number: o.orderNumber });
    setSubject(settings.data?.companyName ? `${base} - ${settings.data.companyName}` : base);
    setBody(t('order.email.body', { number: o.orderNumber }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, detail.data?.id, customer?.email, settings.data?.companyName]);

  const sendMail = useMutation({
    mutationFn: async () => {
      const att = await orderPdfAttachment({
        order: detail.data,
        customer,
        settings: settings.data,
        t,
      });
      return financeApi.sendEmail({
        to,
        subject,
        text: body,
        attachmentBase64: att.base64,
        attachmentName: att.name,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales', 'orders'] });
      onClose();
    },
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('order.email.title')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={sendMail.isPending}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={() => sendMail.mutate()} disabled={sendMail.isPending || !to || !detail.data}>
            {sendMail.isPending ? `${t('email.send')}…` : t('email.send')}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <p className="muted text-sm" style={{ margin: 0 }}>{t('email.intro')}</p>
        <label className="field">
          <span className="field-label">{t('email.to')}</span>
          <input className="input" type="email" value={to} onChange={(e) => setTo(e.target.value)} required />
        </label>
        <label className="field">
          <span className="field-label">{t('email.subject')}</span>
          <input className="input" value={subject} onChange={(e) => setSubject(e.target.value)} required />
        </label>
        <label className="field">
          <span className="field-label">{t('email.message')}</span>
          <textarea className="textarea" rows={7} value={body} onChange={(e) => setBody(e.target.value)} />
        </label>
        {sendMail.isError && <div className="alert danger">{sendMail.error?.message || t('common.loadError')}</div>}
      </div>
    </Modal>
  );
}
